import React from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { formatDateTimeWithMonth } from "../../../../utils/dateTimeUtils";
import { useSelector, useDispatch } from 'react-redux';

interface Patient {
  id: string;
  name: string;
  age: string;
  phone: string;
  collectionTime: string;
}

interface BillRow {
  sr: number;
  testName: string;
  reportingTime: string;
  price: number;
}

interface BillData {
  patient: Patient;
  rows: BillRow[];
  gross: number;
  discount: number;
  paid: number;
  due: number;
}
interface RoleInfo {
  name?: string;
  qualification?: string;
  designation?: string;
}
interface InvoiceFooter {
  mob1?: string;
  mob2?: string;
  address?: string;
}
interface data {
  createdAt?: string;
  pageHeader: {
    logo: string; // Base64 or URL of the logo image
    hospitalName?: string;
  };
  pageFooter?: {
    Pathologist?: RoleInfo;
    Microbiologist?: RoleInfo;
    Biochemist?: RoleInfo;
    Technologist?: RoleInfo;
    invoiceFooter?: InvoiceFooter;
    [key: string]: RoleInfo | InvoiceFooter | undefined;
  };
}

interface BillReportProps {
  open: boolean;
  onClose: () => void;
  data?: any;
}
const calculateAge = (dob: string, referenceDate?: string): string => {
  const birthDate = new Date(dob);
  const today = referenceDate ? new Date(referenceDate) : new Date();
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  
  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Ensure non-negative values
  years = Math.max(0, years);
  months = Math.max(0, months);
  days = Math.max(0, days);
  
  // Return in format: 0y-0m-0d
  return `${years}y-${months}m-${days}d`;
};


const BillReport: React.FC<BillReportProps> = ({ open, onClose, data }) => {
  const profile = useSelector((state: any) => state.profileSlice.profile);
  
  // Helper function to load image as base64
  const loadImageAsBase64 = (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  };
  
  const generatePDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const MARGIN_LEFT = 50;
    const MARGIN_RIGHT = 50;

    // Helper function to draw label-value pairs
    const drawLabelValue = (
      doc: jsPDF,
      label: string,
      value: string | number | undefined,
      x: number,
      y: number,
      labelWidth: number = 0,
      valueAlign: "left" | "right" = "left"
    ) => {
      const labelStr = String(label ?? "");
      const valueStr = String(value ?? "N/A");
      doc.setFont("helvetica", "normal");
      doc.text(labelStr, x, y);
      doc.setFont("helvetica", "bold");
      let valueX;
      if (valueAlign === "right") {
        valueX = 520;
      } else if (labelWidth > 0) {
        valueX = x + labelWidth;
      } else {
        valueX = x + doc.getTextWidth(labelStr) + 2;
      }
      doc.text(valueStr, valueX, y);
      doc.setFont("helvetica", "normal");
    };

    // Header function
     const addHeaderAndPatientInfo = async () => {
         // --- Colors ---
         doc.setTextColor(0, 0, 0);
         doc.setDrawColor(0, 0, 0);
   
         // --- Logo on the left ---
          const logoSize = 55;
         const logoX = 50;
         const logoY = 15;
        if (data?.pageHeader?.logo) {
         doc.addImage(`${data?.pageHeader?.logo || ""}`, "PNG", logoX, logoY, logoSize, logoSize);
        }
         // --- QR Code on the right side of hospital header ---
         // Generate compact QR URL with format: ?tr=TRNumber_ProjectId
         const trNumber = data?.sampleCollection?.trNo || "";
         const projectId = data?.pageHeader?.projectId || "";
         const qrUrl = `${process.env.REACT_APP_QR_BASE_URL}?tr=${trNumber}_${projectId}`;

         const qrSize = 50;
         const qrX = 505;
         const qrY = 14;

                try {
            const qrDataUrl = await QRCode.toDataURL(qrUrl);
            doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, 50);

            // Add text below QR code
            doc.setFontSize(6);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 0);

            const qrText = "Scan QR Code to download Report";

            // Split text into max 2 lines based on QR width
            const lines = doc
              .splitTextToSize(qrText, qrSize)
              .slice(0, 2);

            // Center text under QR
            const textX = qrX + qrSize / 2;
            const textY = qrY + 52;

            doc.text(lines, textX, textY, {
              align: "center",
            });
          } catch (error) {
            console.error("Error generating QR code:", error);
          }


         // --- PHC Reg No (top right, above title) ---
         if (data?.pageHeader?.PHC_REG_NO) {
           doc.setFont("helvetica", "normal");
           doc.setFontSize(9);
           doc.setTextColor(0, 0, 0);
           const phcText = `PHC Reg No: ${data?.pageHeader?.PHC_REG_NO}`;
           const phcWidth = doc.getTextWidth(phcText);
           const phcX = qrX - 50 - phcWidth; // Align to left of QR with 50pt margin
           doc.text(phcText, phcX, 20);
         }

         // --- Main header (centered between logo and QR) ---
         const pageWidth = 595; // A4 width in points
         const logoEnd = logoX + logoSize;
         const availableWidth = qrX - logoEnd; // Space between logo end and QR start
         const centerX = logoEnd + (availableWidth / 2); // Center point between logo and QR
         
         if (data?.pageHeader?.title) {
           doc.setFont("helvetica", "bold");
           doc.setFontSize(22);
           doc.setTextColor(0, 0, 0);
           const titleText = data.pageHeader.title;
           const titleWidth = doc.getTextWidth(titleText);
           const titleX = centerX - (titleWidth / 2);
           doc.text(titleText, titleX, 40);
         }
         
         // --- Subheader (centered between logo and QR) ---
         if (data?.pageHeader?.address) {
           doc.setFont("helvetica", "bold");
           doc.setFontSize(16);
           const addressText = data.pageHeader.address;
           const addressWidth = doc.getTextWidth(addressText);
           const addressX = centerX - (addressWidth / 2);
           doc.text(addressText, addressX, 57);
         }
         
         // --- Contact info line ---
         let currentContactY = 67;
         
         const contactParts = [
           data?.pageHeader?.website,
           data?.pageHeader?.email ? `E-Mail: ${data.pageHeader.email}` : null,
           data?.pageHeader?.phone,
           data?.pageHeader?.phone2
         ].filter(Boolean);
         
         if (contactParts.length > 0) {
           doc.setFontSize(8);
           const contactText = contactParts.join(', ');
           const contactWidth = doc.getTextWidth(contactText);
           const contactX = ((pageWidth - contactWidth) / 2) - 20;
           doc.text(contactText, contactX, currentContactY);
         }

         // --- Header separator line ---
         doc.setDrawColor(0, 0, 0);
         doc.setLineWidth(1);
         doc.line(40, currentContactY + 10, 555, currentContactY + 10);
         
         // --- Patient Information Section (2 columns layout) ---
         doc.setFontSize(9);
         doc.setTextColor(0, 0, 0);
         
         // Define label widths for alignment
         const col1LabelWidth = 90;
         const col2LabelWidth = 100;
         
         const patientDataStartY = currentContactY + 20;
         
         // Column 1 (Left)
         drawLabelValue(
           doc,
           "MR No: ",
           data?.patient?.mrn || "--",
           MARGIN_LEFT,
           patientDataStartY,
           col1LabelWidth
         );

           drawLabelValue(
           doc,
           "Ref MRN: ",
           data?.patient?.ref_mrn || "-",
           MARGIN_LEFT,
           patientDataStartY + 13,
           col1LabelWidth
         );
         drawLabelValue(
           doc,
           "Patient Name: ",
           data.patient.patientName || "--",
           MARGIN_LEFT,
           patientDataStartY + 26,
           col1LabelWidth
         );
         drawLabelValue(
           doc,
           "Guardian: ",
           data.patient?.guardiansName || "--",
           MARGIN_LEFT,
           patientDataStartY + 39,
           col1LabelWidth
         );
         drawLabelValue(
           doc,
           "Age/Sex: ",
           `${calculateAge(data.patient.dob, data?.sampleCollection?.createdAt)} / ${data.patient.gender}`,
           MARGIN_LEFT,
           patientDataStartY + 52,
           col1LabelWidth
         );
         drawLabelValue(
           doc,
           "Phone: ",
           data.patient.phonNumber || "N/A",
           MARGIN_LEFT,
           patientDataStartY + 65,
           col1LabelWidth
         );


         // Column 2 (Right)
         drawLabelValue(
           doc,
           "TR No: ",
           data?.sampleCollection?.trNo || "N/A",
           300,
           patientDataStartY,
           col2LabelWidth
         );
         drawLabelValue(
           doc,
           "Collection Date: ",
           data?.sampleCollection?.collectionDate ? formatDateTimeWithMonth(data.sampleCollection.collectionDate) : "N/A",
           300,
           patientDataStartY + 13,
           col2LabelWidth
         );
        //  drawLabelValue(
        //    doc,
        //    "Reporting Date: ",
        //    data?.sampleCollection?.reportingDate ? formatDateTimeWithMonth(data.sampleCollection.reportingDate) : "N/A",
        //    300,
        //    patientDataStartY + 20,
        //    col2LabelWidth
        //  );
         drawLabelValue(
           doc,
           "Registered At: ",
           data?.collectionCenter?.collectionCenterName || "--",
           300,
           patientDataStartY + 26,
           col2LabelWidth
         );
         drawLabelValue(
           doc,
           "Referred By: ",
           data?.sampleCollection.otherDetails?.referredBy || "--",
           300,
           patientDataStartY + 39,
           col2LabelWidth
         );
         drawLabelValue(
           doc,
           "Collection Type: ",
           data?.sampleCollection.otherDetails?.specimenType || "--",
           300,
           patientDataStartY + 52,
           col2LabelWidth
         );
         
         // --- Bottom separator line ---
         doc.setDrawColor(0, 0, 0);
         doc.setLineWidth(0.5);
         doc.line(40, patientDataStartY + 70, 555, patientDataStartY + 70);
       };

    // Initialize PDF with header
    await addHeaderAndPatientInfo();

    // Bill Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Patient Bill", pageWidth / 2, 170, { align: "center" });

    // Bill Table Lines
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(40, 180, 555, 180);

    // Bill Table
    autoTable(doc, {
      startY: 190,
      head: [["Sr. No", "Test Name", "Reporting Time", "Price(Rs)"]],
      body: data?.rows.map((r: any, i: number) => [
        i + 1,
        r.testName,
        formatDateTimeWithMonth(r.reportingTime),
        `${r.price?.toFixed(2)}`,
      ]),
      theme: "striped",
      styles: {
        fontSize: 9,
        cellPadding: 4,
        halign: "left",
        valign: "middle",
      },
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: [51, 65, 85],
        fontStyle: "bold",
        fontSize: 9,
      },
      columnStyles: {
        0: { cellWidth: 50, halign: "left" },
        1: { cellWidth: 150, halign: "left" },
        2: { cellWidth: 200, halign: "left" },
        3: { cellWidth: 105, halign: "left" },
      },
      margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT, bottom: 120 },
      didDrawPage: data => {
        addHeaderAndPatientInfo();
        data.settings.startY = 220;
      },
    });

    // Summary Section
    const finalY = (doc as any).lastAutoTable.finalY + 20;
    
    // Check if there's enough space for summary (need ~110px for summary section)
    if (finalY > pageHeight - 220) {
      doc.addPage();
      await addHeaderAndPatientInfo();
    }
    
    const summaryY = finalY > pageHeight - 220 ? 190 : finalY;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Bill Summary", 400, summaryY);

    doc.setFont("helvetica", "normal");

    // Starting point
    let startX = 300;
    let startY = summaryY + 30;
    let rowGap = 15;
    let endLine = 550;

    // Row 1: Gross Amount
    drawLabelValue(
      doc,
      "Gross Amount:",
      `${data?.paymentDetail?.grossAmount?.toFixed(2)}`,
      startX,
      startY,
      0,
      "right"
    );
    doc.line(startX, startY + 2, endLine, startY + 2);

    // Row 2: Discount
    startY += rowGap;
    drawLabelValue(
      doc,
      "Discount:",
      `${data?.paymentDetail?.discountInAmount?.toFixed(2)}`,
      startX,
      startY,
      0,
      "right"
    );
    doc.line(startX, startY + 2, endLine, startY + 2);

    // Row 3: Net Amount
    startY += rowGap;
    drawLabelValue(
      doc,
      "Net Amount:",
      `${data?.paymentDetail?.netAmount?.toFixed(2)}`,
      startX,
      startY,
      0,
      "right"
    );
    doc.line(startX, startY + 2, endLine, startY + 2);

    // Row 4: Paid Amount
    startY += rowGap;
    drawLabelValue(
      doc,
      "Paid Amount:",
      `${data?.paymentDetail?.nowPaying?.toFixed(2)}`,
      startX,
      startY,
      0,
      "right"
    );
    doc.line(startX, startY + 2, endLine, startY + 2);

    // Row 5: Due Amount
    startY += rowGap;
    drawLabelValue(
      doc,
      "Due Amount:",
      `${data?.paymentDetail?.dueAmount?.toFixed(2)}`,
      startX,
      startY,
      0,
      "right"
    );
    doc.line(startX, startY + 2, endLine, startY + 2);
    if (data?.paymentDetail?.dueAmount === 0) {
      doc.addImage("paidStamp.png", "PNG", startX + 85, startY - 60, 100, 60);
    }
    // Footer for all pages
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerStartY = pageHeight - 140;

      // View Lab Report Online section
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("View Lab Report Online:", 40, footerStartY - 20);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("To view report online, scan above QR code", 40, footerStartY - 8);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Or", 40, footerStartY + 2);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      const trNumber = data?.sampleCollection?.trNo || "";
      const projectId = data?.pageHeader?.projectId || "";
      const verificationUrl = `${process.env.REACT_APP_QR_BASE_URL}?tr=${trNumber}_${projectId}`;
      doc.text("This report can be viewed online at:", 40, footerStartY + 12);
      doc.setTextColor(0, 0, 0);
      doc.textWithLink(verificationUrl, 212, footerStartY + 12, { url: verificationUrl });
      
      // Test Reference No / MRN (left) and Username / Password (right) — vertically aligned
      const leftLabelX = 40;
      const leftValueX = 160;
      const rightLabelX = 340;
      const rightValueX = 420;
      const row1Y = footerStartY + 24;
      const row2Y = footerStartY + 36;

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Test Reference No:", leftLabelX, row1Y);
      doc.setFont("helvetica", "normal");
      doc.text(String(data?.sampleCollection?.trNo || "N/A"), leftValueX, row1Y);

      const labUsername = String(
        data?.sampleCollection?.auth?.username ?? ""
      ).trim();
      const labPassword = String(
        data?.sampleCollection?.auth?.password ?? ""
      ).trim();

      if (labUsername) {
        doc.setFont("helvetica", "bold");
        doc.text("Username:", rightLabelX, row1Y);
        doc.setFont("helvetica", "normal");
        doc.text(labUsername, rightValueX, row1Y);
      }

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("MRN No:", leftLabelX, row2Y);
      doc.setFont("helvetica", "normal");
      doc.text(String(data?.patient?.mrn || "N/A"), leftValueX, row2Y);

      if (labPassword) {
        doc.setFont("helvetica", "bold");
        doc.text("Password:", rightLabelX, row2Y);
        doc.setFont("helvetica", "normal");
        doc.text(labPassword, rightValueX, row2Y);
      }

      // Electronically verified text
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(
        "Electronically verified. No signature(s) required",
        pageWidth / 2,
        footerStartY + 48,
        { align: "center" }
      );

      // Top line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(2);
      doc.line(40, footerStartY + 53, pageWidth - 40, footerStartY + 53);
      
      // Invoice Footer - Mobile and Address
      const invoiceFooter = data?.pageFooter?.invoiceFooter;
      if (invoiceFooter) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        
        // Mobile numbers
        const mob1Text = invoiceFooter.mob1 || '';
        const mob2Text = invoiceFooter.mob2 || '';
        const mobileText = mob1Text && mob2Text ? `${mob1Text}, ${mob2Text}` : mob1Text || mob2Text;
        
        // Address
        const addressText = invoiceFooter.address || '';
        
        // Combine mobile and address with spacing
        const fullText = mobileText && addressText ? `${mobileText}  ${addressText}` : mobileText || addressText;
        const textWidth = doc.getTextWidth(fullText);
        const iconSize = 8;
        const iconSpacing = 5;
        
        // Calculate total width including icons
        const totalWidth = (mobileText ? iconSize + iconSpacing : 0) + 
                          (addressText && mobileText ? iconSize + iconSpacing : 0) + 
                          textWidth;
        const startX = (pageWidth - totalWidth) / 2;
        
        let currentX = startX;
        
        // Add phone icon and mobile numbers
        if (mobileText) {
          try {
            const phoneIcon = await loadImageAsBase64('/phone.png');
            doc.addImage(phoneIcon, "PNG", currentX, footerStartY + 56, iconSize, iconSize);
          } catch (error) {
            console.error("Error loading phone icon:", error);
          }
          currentX += iconSize + iconSpacing;
          const mobileWidth = doc.getTextWidth(mobileText);
          doc.text(mobileText, currentX, footerStartY + 63);
          currentX += mobileWidth;
        }
        
        // Add spacing between mobile and address
        if (mobileText && addressText) {
          currentX += 15;
        }
        
        // Add location icon and address
        if (addressText) {
          try {
            const locationIcon = await loadImageAsBase64('/location.png');
            doc.addImage(locationIcon, "PNG", currentX, footerStartY + 56, iconSize, iconSize);
          } catch (error) {
            console.error("Error loading location icon:", error);
          }
          currentX += iconSize + iconSpacing;
          doc.text(addressText, currentX, footerStartY + 63);
        }
      }

      // Bottom line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(2);
      doc.line(40, footerStartY + 68, pageWidth - 40, footerStartY + 68);

  
      // Left side: Printing time and Printed By; right side: Registered By
      doc.setFontSize(7);
      
      // Printing Time label (bold and black)
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const printingTimeLabel = `Printing Time : ${ new Date().toLocaleString()} `;
      doc.text(printingTimeLabel, 45, footerStartY + 80);
      
      // Printed By (left)
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const printedByName =
        profile?.data?.fullName ||
        profile?.data?.firstName ||
        data?.pageHeader?.title ||
        "N/A";
      const printedByLabel = `Printed By  :  ${printedByName}`;
      doc.text(printedByLabel, 45, footerStartY + 88);

      // Registered By (right) — who created the sample
      const registeredByName =
        data?.sampleCollection?.createdBy?.fullName ||
        data?.sampleCollection?.createdBy?.firstName ||
        "N/A";
      const registeredByLabel = `Registered By  :  ${registeredByName}`;
      doc.text(registeredByLabel, pageWidth - 45, footerStartY + 88, {
        align: "right",
      });

      // Page number
      doc.setFont("helvetica", "bold");
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 30, {
        align: "center",
      });
    }

    // Print handling
    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };

    window.addEventListener("focus", cleanup);

    iframe.src = blobUrl;
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };

    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Trigger PDF generation if open is true
  if (open) {
    generatePDF();
    onClose();
  }

  return null;
};

export default BillReport;
